import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
} from 'react';
import { FerramentasDaListagem } from '../../shared/components';
import { PageLayout } from '../../shared/layouts';
import {
  DataFactoryService,
  ITenantDataFactory,
} from '../../shared/services/api/data-factory/DataFactoryService';
import { ApiException } from '../../shared/services/api/ApiException';
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridSortModel,
} from '@mui/x-data-grid';
import { useDebounce } from '../../shared/hooks';
import {
  Box,
  Icon,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import { feedback, traducaoComponenteDataGrid } from '../../shared/services';
import { useNavigate } from 'react-router-dom';
import {
  DataFactoryFiltersProvider,
  useDataFactoryFilters,
} from '../../shared/contexts/DataFactoryFiltersContext';
import { useTemPermissao } from '../../shared/contexts/PermissoesContext';

const ListageDatamDataFactoryInner: React.FC = () => {
  const { debounce } = useDebounce();
  const [isLoading, setIsLoading] = useState(true);
  const [dataFactories, setDataFactories] = useState<ITenantDataFactory[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const [rowCount, setRowCount] = useState<number>(0);
  const [searchIdTenant, setSearchIdTenant] = useState<string>('');
  const [monitLoadingId, setMonitLoadingId] = useState<string | null>(null);
  const [migrationLoadingId, setMigrationLoadingId] = useState<string | null>(
    null,
  );
  const [userLoadingId, setUserLoadingId] = useState<string | null>(null);
  const [protocolDialogOpen, setProtocolDialogOpen] = useState<boolean>(false);
  const [protocolValue, setProtocolValue] = useState<string>('');
  const navigate = useNavigate();
  const { onlyActive, setOnlyActive } = useDataFactoryFilters();




  const permiteConsultarTenants = useTemPermissao('clientes.geral.gvdatafactory.consultarTenants');
  const permiteAplicarMigrations = useTemPermissao('clientes.geral.gvdatafactory.aplicarMigrationsTenant');
  const permiteMonitorarTenant = useTemPermissao('clientes.geral.gvdatafactory.monitorarTenant');
  const permitePararMonitoramentoTenant = useTemPermissao('clientes.geral.gvdatafactory.pararMonitoramentoTenant');
  const permiteCriarUsuario = useTemPermissao('clientes.geral.gvdatafactory.criarUsuario');
  const permiteRedefinirSenhaUsuario = useTemPermissao('clientes.geral.gvdatafactory.redefinirSenhaUsuario');

  const loadRows = useCallback(() => {
    const [sort] = sortModel;
    const order = sort?.sort as 'asc' | 'desc' | undefined;
    const sortBy = sort?.field;
    const limite = pageSize;
    const offset = page * pageSize;
    const search = searchIdTenant.trim();

    setIsLoading(true);
    DataFactoryService.getTenants({
      order,
      sortBy,
      limite,
      offset,
      searchIdTenant: search,
      SomenteAtivos: onlyActive,
    }).then((result) => {
      setIsLoading(false);
      if (result instanceof ApiException) {
        feedback(result.message, 'error');
        return;
      }
      setDataFactories(result.data.items);
      setRowCount(
        typeof result.data.totalCount === 'number'
          ? result.data.totalCount
          : result.data.items.length,
      );
    });
  }, [sortModel, page, pageSize, searchIdTenant, onlyActive]);
  useEffect(() => {
    setPage(0);
  }, [searchIdTenant]);

  useEffect(() => {
    debounce(loadRows);
  }, [debounce, loadRows]);

  useEffect(() => {
    setPage(0);
  }, [sortModel]);
  useEffect(() => {
    setPage(0);
  }, [pageSize]);

  const handleMonitoraramento = useCallback(
    async (idTenant: string) => {
      if (!idTenant) return;
      setMonitLoadingId(idTenant);
      const result = await DataFactoryService.monitoraramento(idTenant);
      setMonitLoadingId(null);
      if (result instanceof ApiException) {
        feedback(result.message, 'error');
        return;
      }
      feedback('Monitoramento ativado', 'success');
      loadRows();
    },
    [loadRows],
  );

  const handleDesativaMonitoramento = useCallback(
    async (idTenant: string) => {
      if (!idTenant) return;
      setMonitLoadingId(idTenant);
      const result = await DataFactoryService.deleteMonitoramento(idTenant);
      setMonitLoadingId(null);
      if (result instanceof ApiException) {
        feedback(result.message, 'error');
        return;
      }
      feedback('Monitoramento desativado com sucesso!', 'success');
      loadRows();
    },
    [loadRows],
  );

  const handleAplicaMigration = useCallback(
    async (idTenant: string) => {
      if (!idTenant) return;
      setMigrationLoadingId(idTenant);
      const result = await DataFactoryService.aplicaMigration(idTenant);
      setMigrationLoadingId(null);
      if (result instanceof ApiException) {
        feedback(result.message, 'error');
        return;
      }
      feedback('Migrations aplicadas com sucesso!', 'success');
      loadRows();
    },
    [loadRows],
  );

  const handleCriaUsuario = useCallback(
    async (idTenant: string) => {
      if (!idTenant) return;
      setUserLoadingId(idTenant);
      const result = await DataFactoryService.criaUsuario(idTenant);
      setUserLoadingId(null);
      if (result instanceof ApiException) {
        feedback(result.message, 'error');
        return;
      }
      setProtocolValue(result.data.valor);
      setProtocolDialogOpen(true);
      loadRows();
    },
    [loadRows],
  );

  const handleAtualizaUsuario = useCallback(
    async (idTenant: string) => {
      if (!idTenant) return;
      setUserLoadingId(idTenant);
      const result = await DataFactoryService.atualizaUsuario(idTenant);
      setUserLoadingId(null);
      if (result instanceof ApiException) {
        feedback(result.message, 'error');
        return;
      }
      setProtocolValue(result.data.valor);
      setProtocolDialogOpen(true);
      loadRows();
    },
    [loadRows],
  );

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'actions',
        headerName: 'Ações',
        width: 200,
        sortable: false,
        filterable: false,
        renderCell: (params: GridCellParams) => {
          return (
            <Box display="flex" gap={1}>
              <Tooltip
                title={
                  params.row.ativo === false
                    ? 'Ativar Monitoramento'
                    : 'Desativar Monitoramento'
                }
              >
                <IconButton
                  disabled={monitLoadingId === params.row.idTenant || (permiteMonitorarTenant && params.row.ativo === false) || (permitePararMonitoramentoTenant && params.row.ativo === true)}
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    if (params.row.ativo === false) {
                      handleMonitoraramento(params.row.idTenant);
                    } else {
                      handleDesativaMonitoramento(params.row.idTenant);
                    }
                  }}
                >
                  {params.row.ativo === false ? (
                    <Icon>monitor</Icon>
                  ) : (
                    <Icon>desktop_access_disabled</Icon>
                  )}
                </IconButton>
              </Tooltip>
              {params.row.ativo === true && (
                <Tooltip title="Visualizar detalhes">
                  <IconButton
                    disabled={permiteConsultarTenants}
                    onClick={(e) =>
                      navigate(
                        `/gv-data-factory/detalhe/${params.row.idTenant}`,
                      )
                    }
                  >
                    <Icon>visibility</Icon>
                  </IconButton>
                </Tooltip>
              )}
              {params.row.ativo === true &&
                params.row.usuarioCriado === false && (
                <Tooltip title="Criar usuário">
                  <IconButton
                    disabled={userLoadingId === params.row.idTenant || permiteCriarUsuario}
                    onClick={(e: MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      handleCriaUsuario(params.row.idTenant);
                    }}
                  >
                    <Icon>person_add</Icon>
                  </IconButton>
                </Tooltip>
              )}
              {params.row.ativo === true &&
                params.row.usuarioCriado === true && (
                <Tooltip title="Redefinir senha do usuário">
                  <IconButton
                    onClick={(e: MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      handleAtualizaUsuario(params.row.idTenant);
                    }}
                    disabled={userLoadingId === params.row.idTenant || permiteRedefinirSenhaUsuario}
                  >
                    <Icon>sync_lock</Icon>
                  </IconButton>
                </Tooltip>
              )}
              {params.row.ativo === true && (
                <Tooltip title="Aplicar migrations">
                  <IconButton
                    onClick={(e: MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      handleAplicaMigration(params.row.idTenant);
                    }}
                    disabled={migrationLoadingId === params.row.idTenant || permiteAplicarMigrations}
                  >
                    <Icon>settings_suggest</Icon>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        field: 'idTenant',
        headerName: 'idTenant',
        sortable: true,
        filterable: true,
        flex: 1,
      },
    ];
  }, [
    handleMonitoraramento,
    monitLoadingId,
    handleAplicaMigration,
    migrationLoadingId,
    handleCriaUsuario,
    handleAtualizaUsuario,
    userLoadingId,
  ]);

  return (
    <PageLayout
      alignTools="end"
      title="Gv DataFactory"
      tools={
        <FerramentasDaListagem
          esconderBotaoDeNovo
          textoBotaoDeNovo=""
          valorDaBusca={searchIdTenant}
          aoMudarBusca={setSearchIdTenant}
          mostrarSomenteAtivosSwitch
          somenteAtivos={onlyActive}
          onChangeSomenteAtivos={(checked) => {
            setOnlyActive(checked);
            setPage(0);
            debounce(loadRows);
          }}
        />
      }
    >
      <Box sx={{ display: 'flex', height: '100%' }}>
        <Box sx={{ flexGrow: 1 }}>
          <DataGrid
            rows={dataFactories}
            columns={columns}
            loading={isLoading}
            autoHeight
            getRowId={(row) => row.idTenant}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={(model) => setSortModel(model)}
            pagination
            paginationMode="server"
            page={page}
            onPageChange={(newPage) => setPage(newPage)}
            pageSize={pageSize}
            onPageSizeChange={(newSize) => setPageSize(newSize)}
            rowCount={rowCount}
            rowsPerPageOptions={[10, 25, 50]}
            localeText={traducaoComponenteDataGrid}
            density="compact"
          />
        </Box>
      </Box>

      <Dialog
        open={protocolDialogOpen}
        onClose={() => setProtocolDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Senha gerada</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Senha"
              value={protocolValue}
              InputProps={{ readOnly: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(protocolValue);
                feedback(
                  'Senha copiada para a área de transferência.',
                  'success',
                );
              } catch {
                feedback('Não foi possível copiar o protocolo.', 'error');
              }
            }}
            variant="contained"
          >
            <Icon sx={{ mr: 1 }}>content_copy</Icon>
            Copiar
          </Button>
          <Button onClick={() => setProtocolDialogOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export const ListageDatamDataFactory: React.FC = () => (
  <DataFactoryFiltersProvider>
    <ListageDatamDataFactoryInner />
  </DataFactoryFiltersProvider>
);
